import React from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Eye, RefreshCw } from "lucide-react";
import { cn } from "../../../../utils/cn";
import { Button } from "../../../../components/ui/Button";
import { Skeleton } from "../../../../components/ui/Skeleton";
import { isEndpointMissing } from "../../../../services/aiChatService";
import { showToast } from "../../../../utils/showToast";
import { workflowErrorMessage } from "../../../../components/helpdesk/workflowErrorMessage";

// Piezas comunes de las pestañas Etiquetas / Macros / Automatización: misma
// cabecera de sección, mismos estados de carga, error y solo lectura, para que
// las tres se lean como una sola pantalla (y como Respuestas rápidas).

export function SectionHeader({ title, description, meta, actions }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
      <div className="min-w-0">
        <h2 className="text-base font-semibold text-fg">{title}</h2>
        {description && <p className="mt-0.5 text-sm text-fg-muted max-w-prose">{description}</p>}
        {meta && <p className="mt-1 text-xs text-fg-muted tabular-nums">{meta}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2 shrink-0">{actions}</div>}
    </div>
  );
}

export function RefreshButton({ loading, onClick }) {
  const { t } = useTranslation();
  return (
    <Button
      variant="outline"
      onClick={onClick}
      disabled={loading}
      iconLeft={
        <RefreshCw
          className={cn("w-4 h-4", loading && "animate-spin motion-reduce:animate-none")}
          aria-hidden="true"
        />
      }
    >
      {t("Actualizar")}
    </Button>
  );
}

export function ListSkeleton({ rows = 3, height = "h-[64px]" }) {
  return (
    <div className="rounded-xl border border-border divide-y divide-border" aria-hidden="true">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className={cn("flex items-center gap-3 px-4", height)}>
          <Skeleton className="w-2.5 h-2.5 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-3.5 w-40 rounded" />
            <Skeleton className="h-3 w-64 max-w-full rounded" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function LoadErrorBox({ title, message, onRetry }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-xl border border-border bg-bg-muted p-6 text-center">
      <AlertTriangle className="w-6 h-6 mx-auto text-status-error mb-2" aria-hidden="true" />
      <p className="text-sm font-medium text-fg">{title}</p>
      {message && <p className="text-sm text-fg-muted mt-1">{message}</p>}
      <Button variant="outline" size="sm" className="mt-4" onClick={onRetry}>
        {t("Reintentar")}
      </Button>
    </div>
  );
}

export function ReadOnlyNotice({ children }) {
  return (
    <p className="flex items-start gap-2 rounded-lg border border-border bg-bg-muted px-3 py-2 text-xs text-fg-muted">
      <Eye className="w-3.5 h-3.5 mt-0.5 shrink-0" aria-hidden="true" />
      <span>{children}</span>
    </p>
  );
}

// Error del backend dentro de un formulario (409: nombre duplicado o tope).
export function InlineFormError({ id, children }) {
  if (!children) return null;
  return (
    <div
      id={id}
      role="alert"
      className="flex items-start gap-2 rounded-lg border border-status-error bg-status-error-soft px-3 py-2 text-sm text-status-error"
    >
      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
      <span className="min-w-0 break-words">{children}</span>
    </div>
  );
}

// Un solo criterio de mensajes de error para todo el flujo de trabajo del
// helpdesk (ajustes y detalle): ver components/helpdesk/workflowErrorMessage.
export { workflowErrorMessage };

/** Aviso de un fallo de mutación (el 409 NO pasa por acá: va inline). */
export function toastMutationError(err, fallback, t) {
  if (isEndpointMissing(err)) {
    showToast(t("Esta función está en construcción."), "warning");
    return;
  }
  showToast(workflowErrorMessage(err, fallback), false);
}

/** Normaliza para comparar nombres: sin mayúsculas ni tildes (como el backend). */
export function normalizeName(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");
}
